/**
 * Utility functions for Screener Questions Data Management
 *
 * Approach:
 * 1. SmartApplyApplicationState is the single source of truth for answers
 * 2. On page load, if SmartApply has no answers but findApplicationDrafts does,
 *    copy answers from findApplicationDrafts to SmartApply cache
 * 3. Otherwise, use SmartApply answers as-is
 */

import type { GetScreenerQuestionsDataQuery } from './__generated__/ScreenerQuestionsPage.generated';

/**
 * Check if SmartApplyApplicationState has any screener answers
 */
export function hasSmartApplyScreenerAnswers(
    data: GetScreenerQuestionsDataQuery | undefined,
): boolean {
    const smartApplyData =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;

    const screenerAnswers = smartApplyData?.questionsAndAnswers?.screener || [];

    // Check if there are any non-empty answers
    return screenerAnswers.some((qa) => {
        if (!qa.answer) return false;

        switch (qa.answer.__typename) {
            case 'TextAnswerSmartApplyApplicationState':
                return !!qa.answer.value && qa.answer.value.trim() !== '';
            case 'MultiTextAnswerSmartApplyApplicationState':
                return (
                    !!qa.answer.valueList &&
                    qa.answer.valueList.some((v) => !!v.value && v.value.trim() !== '')
                );
            default:
                return false;
        }
    });
}

/**
 * Convert findApplicationDrafts answers to SmartApplyApplicationState format for cache updates
 */
export function convertDraftAnswersToSmartApplyFormat(
    data: GetScreenerQuestionsDataQuery | undefined,
): Array<{ questionId: string; answer: { value: string } }> {
    if (!data) return [];

    const findDraftsData = data.findApplicationDrafts?.operationDetails?.[0];
    const draftQuestions = findDraftsData?.questionsAndAnswers?.screener || [];

    const convertedAnswers: Array<{ questionId: string; answer: { value: string } }> = [];

    for (const draftQuestion of draftQuestions) {
        if (!draftQuestion.question?.questionId || !draftQuestion.answer) {
            continue;
        }

        const questionId = draftQuestion.question.questionId;
        let answerValue = '';

        // Extract answer value based on type
        switch (draftQuestion.answer.__typename) {
            case 'DraftManagementTextAnswer':
                answerValue = draftQuestion.answer.value || '';
                break;
            case 'DraftManagementMultiTextAnswer':
                // For multi-text, take the first value or join them
                answerValue = draftQuestion.answer.valueList?.[0]?.value || '';
                break;
            default:
                console.warn(`Unknown draft answer type: ${draftQuestion.answer.__typename}`);
                continue;
        }

        // Only add if there's a non-empty answer
        if (answerValue.trim() !== '') {
            convertedAnswers.push({
                questionId,
                answer: { value: answerValue },
            });
        }
    }

    return convertedAnswers;
}

/**
 * Helper function to get question structure from findApplicationDrafts (for display purposes)
 */
export function getQuestionStructure(data: GetScreenerQuestionsDataQuery | undefined) {
    if (!data) return [];

    const findDraftsData = data.findApplicationDrafts?.operationDetails?.[0];
    const draftQuestions = findDraftsData?.questionsAndAnswers?.screener || [];

    return draftQuestions.map((draftQuestion) => {
        const questionDetails = draftQuestion.question?.details;

        return {
            questionId: draftQuestion.question?.questionId || '',
            type: draftQuestion.question?.type || 'TEXT',
            label: extractQuestionLabel(questionDetails) || '',
            options: extractQuestionOptions(questionDetails) || [],
            requirement: extractQuestionRequirement(questionDetails) || '',
        };
    });
}

/**
 * Helper function to get current answers from SmartApplyApplicationState
 */
export function getSmartApplyAnswers(
    data: GetScreenerQuestionsDataQuery | undefined,
): Record<string, string> {
    const smartApplyData =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;

    const screenerAnswers = smartApplyData?.questionsAndAnswers?.screener || [];
    const answers: Record<string, string> = {};

    for (const qa of screenerAnswers) {
        if (!qa.questionId || !qa.answer) continue;

        switch (qa.answer.__typename) {
            case 'TextAnswerSmartApplyApplicationState':
                answers[qa.questionId] = qa.answer.value || '';
                break;
            case 'MultiTextAnswerSmartApplyApplicationState':
                // For multi-text, take the first value
                answers[qa.questionId] = qa.answer.valueList?.[0]?.value || '';
                break;
        }
    }

    return answers;
}

// Helper functions to extract question details from polymorphic types

/**
 * Extracts question label from polymorphic question details
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractQuestionLabel(details: any): string | undefined {
    switch (details?.__typename) {
        case 'DraftManagementSelectQuestion':
        case 'DraftManagementTextBoxQuestion':
        case 'DraftManagementTextAreaQuestion':
        case 'DraftManagementDateQuestion':
        case 'DraftManagementFileQuestion':
        case 'DraftManagementHierarchicalQuestion':
            return details.label;
        default:
            return undefined;
    }
}

/**
 * Extracts question options from polymorphic question details
 */
function extractQuestionOptions(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details: any,
): Array<{ value?: string | null; label?: string | null }> {
    switch (details?.__typename) {
        case 'DraftManagementSelectQuestion':
        case 'DraftManagementHierarchicalQuestion':
            return details.options || [];
        default:
            return [];
    }
}

/**
 * Extracts question requirement from polymorphic question details
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractQuestionRequirement(details: any): string | undefined {
    switch (details?.__typename) {
        case 'DraftManagementSelectQuestion':
        case 'DraftManagementTextBoxQuestion':
        case 'DraftManagementTextAreaQuestion':
        case 'DraftManagementDateQuestion':
        case 'DraftManagementFileQuestion':
        case 'DraftManagementHierarchicalQuestion':
            return details.requirement;
        default:
            return undefined;
    }
}
